/*  
This component renders service inventory page. Which shows image and its version. We can filter based on account, cluster, namespace.

This component calls updated time component, refresh button, download component, dropbox (for selection of aws account), checkbox (for 
selection of cluster and namespace), search input box( filters the table records based on the given input), table component.
*/

import React, { useState, useEffect } from 'react';
import './Home.css';

import Updatedtime from '../UpdatedTime/Updatedtime';
import Dropdown from './Dropbox';
import Table from './Table';
import Download from '../Download/Download';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Home() {
  const [selectedClusters, setSelectedClusters] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [selectedNamespaces, setSelectedNamespaces] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [environments, setEnvironments] = useState([]);
  const [environmentData, setEnvironmentData] = useState([]);
  const [environmentTime, setEnvironmentTime] = useState('');  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleEnvironmentSelect = (env) => {
    setSelectedEnvironment(env);
    setSelectedClusters([]);
    setSelectedNamespaces([]);
  };

  const handleClusterSelect = (clusters) => {
    setSelectedClusters(clusters);
    setSelectedNamespaces([]);
  };

  const handleNamespaceSelect = (namespace) => {
    setSelectedNamespaces(namespace);
  };

// Fetch all environments name
  useEffect(() => {
    const fetchEnv = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/inventory/all-envs`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Initial fetch response:', data);             // Debug log
            
        setEnvironments(data.data || []);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEnv();
  }, []);

  // Fetch environment data when the environment changes
  useEffect(() => {
    if (!selectedEnvironment) {
      setEnvironmentData([]);
      setEnvironmentTime('');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/inventory/${selectedEnvironment}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Initial fetch response:', data);             // Debug log
        
        if (data.date_time === undefined) {
          console.warn('No date_time in response for:', selectedEnvironment);
        }
        
        setEnvironmentData(data.data || []);
        if (data.date_time) {                                      // Only update time if we actually got a value
          setEnvironmentTime(data.date_time);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedEnvironment]);

  const refreshEnv = async () => {
    if (!selectedEnvironment) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('Refreshing environment:', selectedEnvironment);                // Debug log
      
      const response = await fetch(`${API_BASE_URL}/inventory/cache/refresh/${selectedEnvironment}`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Refresh response:', data);                     // Debug log

      if (!data.date_time) {
        console.warn('Refresh: No date_time in response for:', selectedEnvironment);
      }

      setEnvironmentData(data.data || []);
      if (data.date_time) {   
        setSelectedClusters([]);
        setSelectedNamespaces([]);                            // Only update time if we actually got a value
        setEnvironmentTime(data.date_time);
      } else {                                         //Keep the previous time if no new time is provided
        console.log('Keeping previous time due to missing date_time in refresh response');
      }
    } catch (err) {
      console.error('Refresh error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div>
      <div className="content-container">
        <div className="time-container">
          <Updatedtime 
            environmentTime={environmentTime} 
            loading={loading}
            error={error}
          />
          <button 
            onClick={refreshEnv} 
            className="refresh-button"
            data-tooltip="Refresh"
            disabled={!selectedEnvironment || loading}
          >
            ↻
          </button>
          <Download 
            environmentData={environmentData}
            selectedEnvironment={selectedEnvironment}
            selectedClusters={selectedClusters}
            selectedNamespaces={selectedNamespaces}
            environmentTime={environmentTime} 
        />
        </div>
        
        <div className='dropbox-table'>
          <div className="dropbox-container">
            <Dropdown
              onSearch={handleSearch}
              onSelectEnvironment={handleEnvironmentSelect}
              onSelectCluster={handleClusterSelect}
              onSelectNamespace={handleNamespaceSelect}
              environments = {environments}
              selectedClusters={selectedClusters}
              environmentData={environmentData}
            />
          </div>
          
          <div className="table-container">
            <Table
              searchTerm={searchTerm}
              selectedClusters={selectedClusters}
              selectedEnvironment={selectedEnvironment}
              selectedNamespaces={selectedNamespaces}
              environmentData={environmentData}
              loading={loading}
              error={error}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
